import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, generatePath } from "react-router-dom";
import cn from "classnames";
import Styles from "./PlayerSearchPage.module.scss";
import Sticky from "../../atoms/Sticky/StickyItem.module.scss";
import { Text } from "../../atoms/Text/Text";
import { Title } from "src/components/atoms/Title/Title";
import { SearchInput } from "src/components/compounds/SearchInput/SearchInput";
import { ROUTES } from "src/utils/constants/routes";
import { useSearchPlayersQuery } from "src/state/modules/api";
import * as Eden from "src/eden/content";

const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;

// Short hand (e.g. "right" -> "R") for the bats/throws meta line.
const handed = (side?: string | null) => (side ? side.charAt(0).toUpperCase() : null);
const fullName = (p: Eden.PlayerSearchResult) => `${p.first_name} ${p.last_name}`.trim();

export default function PlayerSearchPage() {
  const [name, setName] = useState("");
  const [debounced, setDebounced] = useState("");

  // Only auto-focus on pointer (desktop) devices — see the same note in the team SearchPage.
  const autoFocusSearch = useMemo(
    () => typeof window !== "undefined" && !!window.matchMedia?.("(pointer: fine)").matches,
    []
  );

  // Debounce the typed query so we hit the backend scan once the user pauses, not per keystroke.
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(name.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [name]);

  const hasQuery = debounced.length >= MIN_QUERY_LENGTH;
  const { data, isFetching } = useSearchPlayersQuery(debounced, { skip: !hasQuery });

  const ours = data?.our_players ?? [];
  const opponents = data?.opponents ?? [];
  const noResults = hasQuery && !isFetching && !!data && ours.length === 0 && opponents.length === 0;

  return (
    <div className={cn(Styles.playerSearchWrapper)}>
      <Title text="Players" />
      <div
        className={cn(Styles.searchHeader, Sticky.stickyItem)}
        data-sticky-name="player-search-header"
        data-sticky="true"
      >
        <form onSubmit={(event: FormEvent) => event.preventDefault()} name="query">
          <SearchInput
            handleChange={(event) => setName(event.currentTarget.value)}
            placeholder="Find a player by name or number"
            autoFocus={autoFocusSearch}
          />
        </form>
      </div>

      <section aria-label="Player search results">
        {isFetching && (
          <Text className={Styles.statusMessage} size="small" color="cool-grey-dark" block>
            Searching…
          </Text>
        )}

        {ours.length > 0 && (
          <PlayerSection title="Our Players" total={data?.our_total ?? ours.length} shown={ours.length}>
            {ours.map((player) => (
              <OurPlayerRow key={player.player_id} player={player} />
            ))}
          </PlayerSection>
        )}

        {opponents.length > 0 && (
          <PlayerSection title="Opponents" total={data?.opponent_total ?? opponents.length} shown={opponents.length}>
            {opponents.map((player) => (
              <OpponentRow key={player.player_id} player={player} />
            ))}
          </PlayerSection>
        )}

        {noResults && (
          <Text className={Styles.statusMessage} size="small" color="cool-grey-dark" block>
            No players found. Try a different name or jersey number.
          </Text>
        )}
      </section>
    </div>
  );
}

function PlayerSection({
  title,
  total,
  shown,
  children,
}: {
  title: string;
  total: number;
  shown: number;
  children: React.ReactNode;
}) {
  return (
    <div className={Styles.section}>
      <div className={Styles.sectionHeader}>
        <Text size="small" weight="xbold" color="off-black">
          {title}
        </Text>
        <Text size="small" color="cool-grey-dark">
          {total > shown ? `Showing ${shown} of ${total}` : `${total}`}
        </Text>
      </div>
      <ul className={Styles.list}>{children}</ul>
    </div>
  );
}

// Our players have a real profile page, so the whole row is a link to it.
function OurPlayerRow({ player }: { player: Eden.PlayerSearchResult }) {
  const to = generatePath(ROUTES.teams.playerProfile, { id: player.team_id, pid: player.player_id });
  const bats = handed(player.batting_side);
  const throws = handed(player.throwing_hand);
  return (
    <li>
      <Link to={to} className={Styles.row}>
        <span className={Styles.number}>
          <Text size="small" color="cool-grey-dark" weight="bold">
            {player.number ? `#${player.number}` : "—"}
          </Text>
        </span>
        <span className={Styles.identity}>
          <span className={Styles.name}>
            <Text size="base" color="off-black" weight="semibold">
              {fullName(player) || "Unnamed player"}
            </Text>
          </span>
          <span className={Styles.meta}>
            <Text size="small" color="cool-grey-dark">
              {player.team_name}
            </Text>
            {bats && (
              <Text className={Styles.dot} size="small" color="cool-grey-dark">
                {`Bats ${bats}`}
              </Text>
            )}
            {throws && (
              <Text className={Styles.dot} size="small" color="cool-grey-dark">
                {`Throws ${throws}`}
              </Text>
            )}
          </span>
        </span>
      </Link>
    </li>
  );
}

// Opponents have no profile in our app, so the row isn't a link; instead each links out to the
// schedule of the team(s) of ours that faced them.
function OpponentRow({ player }: { player: Eden.PlayerSearchResult }) {
  return (
    <li className={Styles.row}>
      <span className={Styles.number}>
        <Text size="small" color="cool-grey-dark" weight="bold">
          {player.number ? `#${player.number}` : "—"}
        </Text>
      </span>
      <span className={Styles.identity}>
        <span className={Styles.name}>
          <Text size="base" color="off-black" weight="semibold">
            {fullName(player) || "Unknown player"}
          </Text>
        </span>
        <span className={Styles.meta}>
          <Text size="small" color="cool-grey-dark">
            {player.team_name || "Opponent"}
          </Text>
        </span>
        {player.faced_by.length > 0 && (
          <span className={Styles.facedBy}>
            <Text size="small" color="cool-grey-dark">
              Faced by:
            </Text>
            {player.faced_by.map((team) => (
              <Link
                key={team.team_id}
                to={generatePath(ROUTES.teams.schedule, { id: team.team_id })}
                className={Styles.facedByChip}
              >
                <Text size="small" color="gc-blue" weight="semibold">
                  {team.team_name}
                </Text>
              </Link>
            ))}
          </span>
        )}
      </span>
    </li>
  );
}
